import { Link } from 'react-router-dom';
import { usePageTitle } from '../../hooks/usePageTitle';
import { STRIPE_LINKS } from '../../lib/stripe';
import CalculatorLayout from '../../components/calculator/CalculatorLayout';

const CANONICAL = 'https://floridaaimarketingexperts.com/calculators';

const CALCULATORS = [
  {
    slug: 'missed-calls',
    title: 'Missed Call Revenue Loss Calculator',
    tagline: 'See exactly how much money your business loses every month to unanswered calls.',
    icon: 'ri-phone-off-line',
    accent: 'from-orange-500 to-orange-400',
  },
  {
    slug: 'ai-receptionist-roi',
    title: 'AI Receptionist ROI Calculator',
    tagline: 'Payback period and ROI multiple for a 24/7 AI front desk.',
    icon: 'ri-robot-2-line',
    accent: 'from-navy-800 to-navy-900',
  },
  {
    slug: 'lead-value',
    title: 'Lead Value Calculator',
    tagline: 'Know the true dollar value of one qualified lead — close rate + LTV built in.',
    icon: 'ri-money-dollar-circle-line',
    accent: 'from-navy-800 to-navy-900',
  },
  {
    slug: 'seo-traffic-value',
    title: 'Local SEO Traffic Value Calculator',
    tagline: 'Turn organic traffic into projected revenue at 2x and 5x.',
    icon: 'ri-search-line',
    accent: 'from-navy-900 to-navy-800',
  },
  {
    slug: 'review-generation-roi',
    title: 'Review Generation ROI Calculator',
    tagline: 'Revenue lift from half a star more on Google. 9% benchmark, your numbers.',
    icon: 'ri-star-smile-line',
    accent: 'from-orange-500 to-orange-400',
  },
];

export default function CalculatorsIndexPage() {
  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Free Marketing & Revenue Calculators for Florida Businesses',
    url: CANONICAL,
    description:
      'Five free calculators for Florida businesses: missed call revenue, AI receptionist ROI, lead value, local SEO traffic value, and Google review ROI.',
    hasPart: CALCULATORS.map((c) => ({
      '@type': 'SoftwareApplication',
      name: c.title,
      applicationCategory: 'BusinessApplication',
      url: `https://floridaaimarketingexperts.com/calculator/${c.slug}`,
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    })),
  };

  usePageTitle({
    title: 'Free Marketing & Revenue Calculators for Florida Businesses | FAME',
    description:
      'Five free calculators built for Florida businesses. Missed call revenue, AI receptionist ROI, lead value, local SEO traffic value, and Google review ROI. No signup required.',
    keywords:
      'business calculators, marketing calculator, missed call calculator, ai receptionist roi, lead value calculator, local seo roi calculator, google review roi',
    canonical: CANONICAL,
    schema: collectionSchema,
  });

  return (
    <CalculatorLayout>
      <section className="relative overflow-hidden bg-white grid-bg py-16 md:py-20">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(13,148,136,0.14), transparent 58%)' }}
        ></div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center flex flex-col items-center">
          <div className="eyebrow">
            <i className="ri-calculator-line"></i>Free · No Signup Required
          </div>
          <h1 className="mt-5 h-display text-4xl md:text-6xl leading-[0.95] tracking-[-0.02em]">
            Free Marketing & Revenue Calculators
          </h1>
          <p className="mt-5 text-lg md:text-xl text-slate-600 font-body font-light max-w-3xl">
            Five calculators built for Florida businesses. Missed call loss, AI receptionist ROI,
            lead value, SEO traffic value, and Google review ROI. Real benchmarks. Real math.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {CALCULATORS.map((c) => (
              <Link
                key={c.slug}
                to={`/calculator/${c.slug}`}
                className="fame-card p-8 hover:shadow-2xl transition-all group flex items-start gap-5"
              >
                <div
                  className={`w-16 h-16 bg-gradient-to-br ${c.accent} rounded-xl flex items-center justify-center flex-shrink-0`}
                >
                  <i className={`${c.icon} text-white text-3xl`}></i>
                </div>
                <div>
                  <h2 className="text-navy-900 text-xl md:text-2xl font-display font-bold mb-2 group-hover:text-teal-600 transition-colors">
                    {c.title}
                  </h2>
                  <p className="text-navy-600 text-base mb-3">{c.tagline}</p>
                  <span className="text-teal-600 text-sm font-bold">
                    Open Calculator <i className="ri-arrow-right-line ml-1"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-navy-50">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-navy-900 text-2xl md:text-3xl font-display font-bold mb-4 text-center">
            Why we built these calculators
          </h2>
          <p className="text-navy-600 text-base md:text-lg leading-relaxed mb-4 text-center">
            Most Florida businesses make marketing decisions on gut feel. These calculators give you
            the actual math — in under 30 seconds. Use them before you spend a dollar on ads, call
            another lead vendor, or sign another annual SEO contract. If the numbers do not make
            sense, do not do it.
          </p>
          <div className="mt-8 text-center">
            <a
              href={STRIPE_LINKS.T10_STARTER}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-block"
            >
              Start a $2,500/mo Starter Retainer
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </div>
        </div>
      </section>
    </CalculatorLayout>
  );
}
